import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import AboutSubNav from '../components/AboutSubNav';
import { CAPABILITIES } from '../mock/mock';

const SECTIONS = [
  {
    to: '/about/history',
    tag: 'History',
    title: 'A sawmill, a move to Jaipur and the making of RCD.',
    desc: 'Follow the decisions that took the family enterprise from timber in Jhunjhunu to furniture manufacturing in Jaipur and the creation of Rajdhani Crafts & Daughters.',
    image: 'https://images.pexels.com/photos/31567149/pexels-photo-31567149.jpeg'
  },
  {
    to: '/about/founder',
    tag: 'Family Leadership',
    title: 'Three distinct roles in one family enterprise.',
    desc: 'Meet Samir, Poorvi and Sakshi Agarwal and see how manufacturing experience, business development, operations and product leadership come together at RCD.',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/658jqxhg_IMG_7707.webp',
    rotateFix: true
  },
  {
    to: '/about/clients',
    tag: 'Clients',
    title: 'The people and organisations RCD manufactures for.',
    desc: 'See the hotel groups, architects, designers, procurement teams, retailers, institutions and international buyers served through RCD\u2019s work.',
    image: 'https://images.unsplash.com/photo-1677129667171-92abd8740fa3'
  }
];

const About = () => {
  return (
    <div>
      {/* Hero */}
      <section className="pt-24 pb-16 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">About RCD</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            Six decades of making. A <span className="italic text-[#a67c3d]">new generation</span> in the name.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4a3f36] leading-relaxed">
            Rajdhani Crafts &amp; Daughters carries forward a family enterprise whose relationship with wood began in 1964. Across three generations, it has grown from a sawmill in Jhunjhunu into furniture and engineered wood flooring manufacturing in Jaipur.
          </p>
        </div>
      </section>

      <AboutSubNav />

      {/* Our Legacy */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-6">Our Legacy</p>
          <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight mb-8">
            A family enterprise with <span className="italic text-[#a67c3d]">daughters at the centre</span> of its future.
          </h2>
          <p className="font-serif text-xl md:text-2xl text-[#1a1614] italic leading-snug max-w-3xl mx-auto">
            &ldquo;A family that built its roots in timber, grew through furniture, and now evolves through a new generation of women.&rdquo;
          </p>
          <div className="w-12 h-px bg-[#a67c3d] mx-auto mt-8 mb-6" />
          <p className="text-base text-[#4a3f36] leading-relaxed max-w-3xl mx-auto">
            Beginning with a sawmill in Jhunjhunu in 1964, the family built the timber knowledge that still underpins the business. In 1993, Samir Agarwal moved the enterprise to Jaipur and led its transition into furniture manufacturing. After learning the business from within, Poorvi and Sakshi Agarwal established Rajdhani Crafts and Daughters in 2021. The name makes their leadership &mdash; and their responsibility for RCD&rsquo;s future &mdash; visible.
          </p>
        </div>
      </section>

      {/* Three section entry cards */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SECTIONS.map((s, i) => (
              <Link
                key={s.to}
                to={s.to}
                className="group block bg-white overflow-hidden"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#1a1614]">
                  {s.rotateFix ? (
                    <div
                      className="absolute"
                      style={{
                        top: '50%',
                        left: '50%',
                        width: '133.33%',
                        height: '75%',
                        transform: 'translate(-50%, -50%) rotate(90deg)',
                        transformOrigin: 'center center'
                      }}
                    >
                      <img
                        src={s.image}
                        alt={s.tag}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"
                        style={{ objectPosition: 'center 62%', transform: 'scale(1.25)' }}
                      />
                    </div>
                  ) : (
                    <img
                      src={s.image}
                      alt={s.tag}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"
                    />
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs tracking-[0.28em] uppercase text-[#a67c3d]">0{i + 1} &middot; {s.tag}</span>
                    <ArrowUpRight className="text-[#a67c3d] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={18} />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#1a1614] mb-3 leading-snug group-hover:text-[#a67c3d] transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#4a3f36] leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">Our Capabilities</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1a1614] leading-tight">
              End-to-end craft, <span className="italic text-[#a67c3d]">engineered for global markets</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {CAPABILITIES.map((c, i) => (
              <div key={c.title} className="border-t border-[#e0d6c5] pt-6 group">
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="font-serif text-lg text-[#a67c3d]">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-[#1a1614] group-hover:text-[#a67c3d] transition-colors">{c.title}</h3>
                </div>
                <p className="text-[#4a3f36] leading-relaxed ml-8">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
